import React from 'react';
import lodash from 'lodash';
import { connect } from 'react-redux';
import {
    getStudyTypesByInstitution,
    getTeamMembersByInstitution,
    getStudyRolesByInstitution,
    getStudyMetadata,
    getStudyStages,
    setPotentialResearchersToStudyTeam,
    saveStudy,
    clearStudy,
    discardStudy,
} from '../actions/createStudyV2';
import { updateDraft } from '../actions/updateDraft';
import { initializeStudyWorkflow, setField } from '../actions/workflow';
import { addApplicationMessage } from 'common/actions/applicationMessagesActions';
import {
    loadStudy,
    updateStudy,
    deleteDraftStudy,
} from '../actions/editStudy';

import StudyType from './steps/StudyType';
import StudyTeam from './steps/StudyTeamV2';
import StudyDetails from './steps/StudyDetails';
import StudyEssential from './steps/StudyEssential';

import Workflow, { Step } from 'components/common/Workflow/Workflow';
import { LoadingStatus } from 'components/common/LoadingStatus/LoadingStatus';
import { ExitModal } from './modals/ExitModal';
import { DeleteDraftStudyModal } from './modals/DeleteDraftStudyModal';

const DRAFT_MODE = 'draft';
const ACTIVATE_MODE = 'activate';

export class CreateStudy extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            exitModalOpen: false,
            showDeleteDraftStudyModal: false,
            nextLocation: '',
            isFirstStepComplete: props.isFirstStepComplete,
        };

        this.openExitModal = this.openExitModal.bind(this);
        this.closeExitModal = this.closeExitModal.bind(this);
        this.openDeleteDraftStudyModal = this.openDeleteDraftStudyModal.bind(this);
        this.closeDeleteDraftStudyModal = this.closeDeleteDraftStudyModal.bind(this);
        this.goToNextLocation = this.goToNextLocation.bind(this);
        this.routerWillLeave = this.routerWillLeave.bind(this);
        this.saveAsDraft = this.saveAsDraft.bind(this);
        this.saveAndActivate = this.saveAndActivate.bind(this);
        this.onDiscard = this.onDiscard.bind(this);
        this.redirectToStudies = this.redirectToStudies.bind(this);
    }

    componentDidMount() {
        const { router, route, initializeStudyWorkflow } = this.props;

        initializeStudyWorkflow();
        this.loadInitialWorkflowData();
        this.unregisterLeaveHook = router.setRouteLeaveHook(route, this.routerWillLeave);
    }

    loadInitialWorkflowData() {
        const {
            getStudyTypesByInstitution,
            getStudyRolesByInstitution,
            getTeamMembersByInstitution,
            getStudyStages,
            loadStudy,
            existingStudyId,
            institutionId,
            isExistingDraft,
        } = this.props;

        if (isExistingDraft) {
            loadStudy(existingStudyId);
        }

        getStudyStages();
        getStudyTypesByInstitution(institutionId);
        getStudyRolesByInstitution(institutionId);
        getTeamMembersByInstitution(institutionId);
    }

    routerWillLeave(location) {
        const { isFirstStepComplete } = this.props;
        this.setNextLocation(location.pathname);
        isFirstStepComplete ? this.openExitModal() : this.goToNextLocation();
        return false;
    }

    setNextLocation(path) {
        this.setState({
            nextLocation: path
        });
    }

    goToNextLocation() {
        const { nextLocation } = this.state;
        const { router } = this.props;
        this.unregisterLeaveHook();
        if (nextLocation) {
            return router.push(nextLocation);
        }
        return this.redirectToStudies();
    }

    componentDidUpdate(prevProps) {
        const {
            institutionId,
            getStudyMetadata,
            binderId,
            existingStudyId
        } = this.props;

        if (binderId && existingStudyId) {
            this.unregisterLeaveHook();
            this.redirectToStudy(existingStudyId);
        }

        const selectedStudyType = lodash.get(this.props, 'study.fields.studyType', {});
        const previousStudyType = lodash.get(prevProps, 'study.fields.studyType', {});

        if (selectedStudyType.id !== previousStudyType.id) {
            const { id, binderTemplateId } = selectedStudyType;
            getStudyMetadata(institutionId, binderTemplateId, id);
        }

    }

    componentWillUnmount() {
        this.unregisterLeaveHook();
        this.props.clearStudy();
    }

    saveStudy(status) {
        const { saveStudy } = this.props;
        const { nextLocation } = this.state;
        return saveStudy(status, nextLocation);
    }

    updateDraft() {
        const { existingStudyId, updateDraft } = this.props;
        const { nextLocation } = this.state;
        updateDraft(existingStudyId, nextLocation);
    }

    activateStudy(status) {
        const { existingStudyId, updateStudy } = this.props;
        const { nextLocation } = this.state;
        updateStudy(existingStudyId, status, nextLocation);
    }

    openExitModal() {
        this.setState({ exitModalOpen: true });
    }

    closeExitModal() {
        this.setState({
            exitModalOpen: false,
            nextLocation: ''
        });
    }

    openDeleteDraftStudyModal() {
        this.setState({ showDeleteDraftStudyModal: true });
    }

    closeDeleteDraftStudyModal() {
        this.setState({ showDeleteDraftStudyModal: false });
    }

    onDiscard() {
        this.unregisterLeaveHook();
        this.props.discardStudy('Changes made to draft study discarded.');
    }

    redirectToStudies() {
        const { router } = this.props;
        router.push('/studies');
    }

    redirectToStudy(studyId) {
        const { router } = this.props;
        router.push(`/study/${studyId}`);
    }

    saveAsDraft() {
        this.unregisterLeaveHook();
        if (this.props.isExistingDraft) {
            return this.updateDraft();
        }
        return this.saveStudy(DRAFT_MODE);
    }

    saveAndActivate() {
        this.unregisterLeaveHook();
        if (this.props.isExistingDraft) {
            return this.activateStudy(ACTIVATE_MODE);
        }
        return this.saveStudy(ACTIVATE_MODE);
    }

    render() {
        const {
            study,
            setField,
            saving,
            setPotentialResearchersToStudyTeam,
            addApplicationMessage,
            institutionId,
            isExistingDraft,
            workflowTitle,
            studyName,
            studyTypeName,
            isReturning,
            isCreatingNew,
        } = this.props;


        if (isReturning) {
            return <LoadingStatus
                itemName='Returning to Study'
                actionLabel=''
                loading={true}
                filterQuery={false} />;
        }

        if (isCreatingNew) {
            return <LoadingStatus
                itemName='Create New Study'
                loading={true}
                filterQuery={false} />;
        }

        if (saving) {
            return <LoadingStatus
                itemName='Study'
                actionLabel='Saving'
                loading={true}
                filterQuery={false} />;
        }

        return (
            <div data-cy="CreateStudy">
                <Workflow
                    title={this.state.isFirstStepComplete ? studyName : workflowTitle}
                    subTitle={this.state.isFirstStepComplete && studyTypeName}
                    cancelButtonText='Delete Draft'
                    onCancel={this.openDeleteDraftStudyModal}
                    showCancelButton={isExistingDraft}
                    onCompleteWorkflow={this.saveAndActivate}
                    sharedStepData={study}
                    onNextStep={() => this.setState({ ...this.state, isFirstStepComplete: true })}
                >
                    <Step
                        StepComponent={StudyType}
                        title={'Study Name and Type'}
                        setField={setField}
                        onExit={this.redirectToStudies}
                    />
                    <Step
                        StepComponent={StudyTeam}
                        title={'Study Team'}
                        setField={setField}
                        mapStudyMemberToStudyDetails={setPotentialResearchersToStudyTeam}
                        addApplicationMessage={addApplicationMessage}
                        onExit={this.redirectToStudies}
                    />
                    <Step
                        StepComponent={StudyEssential}
                        title={'Study Details'}
                        setField={setField}
                        onExit={this.redirectToStudies}
                    />
                    <Step
                        StepComponent={StudyDetails}
                        title={'Study Setup'}
                        setField={setField}
                        onExit={this.redirectToStudies}
                    />
                </Workflow>

                <ExitModal
                    isOpen={this.state.exitModalOpen}
                    onClose={this.closeExitModal}
                    onDiscard={this.onDiscard}
                    onSave={this.saveAsDraft}
                />
                <DeleteDraftStudyModal
                    isOpen={this.state.showDeleteDraftStudyModal}
                    onClose={this.closeDeleteDraftStudyModal}
                    deleteDraftStudy={this.props.deleteDraftStudy}
                    goToNextLocation={this.goToNextLocation}
                    institutionId={institutionId}
                    study={study}
                />
            </div>
        );
    }
}

export default connect((state, props) => {
    const institutionId = lodash.get(state, 'me.institution.id');
    const dataSources = lodash.get(state, 'dataSourceItems');
    const study = lodash.get(state, 'study', {});
    const saving = lodash.get(state, 'study.saving', false);
    const studyName = lodash.get(state, 'study.fields.studyName');
    const studyTypeName = lodash.get(state, 'study.fields.studyType.name');
    const binderId = lodash.get(state, 'study.originalFields.binderId');

    study.dataSources = dataSources;

    const existingStudyId = lodash.get(props, 'location.query.sid');
    const studyWorkflowMode = lodash.get(props, 'route.path');
    const isExistingDraft = studyWorkflowMode === DRAFT_MODE;
    const isFirstStepComplete = !lodash.isNil(studyTypeName) && !lodash.isNil(studyName);
    const workflowTitle = isExistingDraft ? 'Draft Study' : 'Create New Study';

    const isReturning = isExistingDraft && !study.originalFields;
    const isCreatingNew = !isExistingDraft && (!study.studyTypes || !study.teamMembers);


    return {
        existingStudyId,
        institutionId,
        study,
        saving,
        studyName,
        studyTypeName,
        isExistingDraft,
        isFirstStepComplete,
        workflowTitle,
        isReturning,
        isCreatingNew,
        binderId
    };
}, {
    getStudyTypesByInstitution,
    getStudyRolesByInstitution,
    getTeamMembersByInstitution,
    getStudyStages,
    getStudyMetadata,
    initializeStudyWorkflow,
    setPotentialResearchersToStudyTeam,
    setField,
    saveStudy,
    clearStudy,
    loadStudy,
    updateStudy,
    updateDraft,
    discardStudy,
    deleteDraftStudy,
    addApplicationMessage,
})(CreateStudy);
